import { useState, useRef } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useLanguage } from '../../context/LanguageContext';
import { PARTS, isMultiSelect, CATEGORIES } from '../../data/parts';
import styles from './PartSelector.module.css';

const PartSelector = ({ category }) => {
    const { config, updateConfig, isPartSelected } = useConfig();
    const { t } = useLanguage();
    const [expandedPart, setExpandedPart] = useState(null);

    // 排气声音播放状态
    const [playingSound, setPlayingSound] = useState(null);
    const audioRef = useRef(null);

    // 播放排气声音
    const handlePlaySound = (e, soundUrl, partId) => {
        e.stopPropagation(); // 防止触发卡片选择

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        if (playingSound === partId) {
            // 如果正在播放同一个，则停止
            setPlayingSound(null);
            return;
        }

        // 播放新的声音
        const audio = new Audio(soundUrl);
        audioRef.current = audio;
        setPlayingSound(partId);

        audio.play().catch(err => {
            console.error('Failed to play sound:', err);
            setPlayingSound(null);
        });

        audio.onended = () => {
            setPlayingSound(null);
            audioRef.current = null;
        };
    };

    const parts = PARTS[category];
    const categoryInfo = CATEGORIES.find(c => c.id === category);
    const isMulti = isMultiSelect(category);

    if (!parts) return null;

    // 获取翻译文本 - 支持嵌套结构
    const getPartName = (part) => {
        const nameKey = part.nameKey;
        // 尝试直接获取翻译
        const directTranslation = t(nameKey);
        if (directTranslation !== nameKey) {
            return directTranslation;
        }
        // 如果 nameKey 是 parts.body.vivid_black 这样的格式
        // 尝试获取嵌套翻译
        return part.name || nameKey;
    };

    // BODY 分类 - 颜色选择
    if (category === 'body') {
        return (
            <div className={styles.colorSection}>
                <div className={styles.swatchGrid}>
                    {parts.map((part) => {
                        const selected = isPartSelected(category, part.id);
                        return (
                            <div
                                key={part.id}
                                className={`${styles.colorOption} ${selected ? styles.colorSelected : ''}`}
                                onClick={() => updateConfig(category, part.id)}
                            >
                                <div
                                    className={`${styles.swatch} ${selected ? styles.swatchSelected : ''}`}
                                    style={{ backgroundColor: part.colorCode }}
                                    title={getPartName(part)}
                                >
                                    {part.isTwoTone && (
                                        <div
                                            className={styles.twoToneOverlay}
                                            style={{ backgroundColor: '#000000' }}
                                        />
                                    )}
                                </div>
                                <div className={styles.colorInfo}>
                                    <span className={styles.colorName}>{getPartName(part)}</span>
                                    {part.price > 0 ? (
                                        <span className={styles.colorPrice}>+${part.price.toLocaleString()}</span>
                                    ) : (
                                        <span className={styles.colorDefault}>{t('parts.price_included')}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // TRIM 分类 - 简单单选
    if (category === 'trim') {
        return (
            <div className={styles.trimSection}>
                {parts.map((part) => {
                    const selected = isPartSelected(category, part.id);
                    return (
                        <div
                            key={part.id}
                            className={`${styles.trimOption} ${selected ? styles.selected : ''}`}
                            onClick={() => updateConfig(category, part.id)}
                        >
                            <span className={styles.trimName}>{getPartName(part)}</span>
                            {part.isDefault && (
                                <span className={styles.defaultBadge}>Default</span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // COMFORT / PERFORMANCE / STYLE - 选装包展示
    // 分离官方配件和第三方配件
    const officialParts = parts.filter(p => !p.isThirdParty);
    const thirdPartyParts = parts.filter(p => p.isThirdParty);

    // 渲染第三方配件卡片
    const renderThirdPartyCard = (part) => {
        const selected = isPartSelected(category, part.id);
        const isExpanded = expandedPart === part.id;

        return (
            <div
                key={part.id}
                className={`${styles.packageCard} ${styles.thirdPartyCard} ${selected ? styles.selected : ''}`}
            >
                <div
                    className={styles.packageHeader}
                    onClick={() => updateConfig(category, part.id)}
                >
                    {/* 品牌标识区域 */}
                    <div className={styles.brandHeader}>
                        <div className={styles.brandInfo}>
                            <span className={styles.brandBadge}>
                                {t('parts.thirdParty.badge')}
                            </span>
                            <span className={styles.brandName}>
                                {t('parts.thirdParty.by')} {part.brand}
                            </span>
                        </div>
                        {part.sku && (
                            <span className={styles.skuLabel}>
                                {t('parts.thirdParty.sku')}: {part.sku}
                            </span>
                        )}
                    </div>

                    {/* 产品图片 */}
                    {part.image && (
                        <div className={styles.productImageContainer}>
                            <img
                                src={part.image}
                                alt={getPartName(part)}
                                className={styles.productImage}
                            />
                        </div>
                    )}

                    {/* 产品名称和选择状态 */}
                    <div className={styles.packageTitleRow}>
                        <span className={styles.packageName}>{getPartName(part)}</span>
                        <span className={styles.checkMark}>
                            {isMulti ? (selected ? '✓' : '+') : (selected ? '●' : '○')}
                        </span>
                    </div>

                    {/* 产品描述 */}
                    {part.descriptionKey && (
                        <p className={styles.productDescription}>
                            {t(part.descriptionKey)}
                        </p>
                    )}

                    {/* 价格信息 */}
                    <div className={styles.packageMeta}>
                        <span className={styles.packagePrice}>
                            ${part.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                        {part.itemCount > 0 && (
                            <span className={styles.itemCount}>
                                {part.itemCount} {t('parts.package_items')}
                            </span>
                        )}
                    </div>
                </div>

                {/* 排气声音播放按钮 */}
                {part.soundUrl && (
                    <button
                        className={`${styles.soundPlayBtn} ${playingSound === part.id ? styles.playing : ''}`}
                        onClick={(e) => handlePlaySound(e, part.soundUrl, part.id)}
                        title={t('parts.thirdParty.listenSound') || 'Listen to exhaust sound'}
                    >
                        <span className={styles.soundIcon}>
                            {playingSound === part.id ? '⏹' : '🔊'}
                        </span>
                        <span className={styles.soundLabel}>
                            {playingSound === part.id ? 'Stop' : 'Listen'}
                        </span>
                    </button>
                )}

                {/* 产品特点列表 */}
                {part.features && part.features.length > 0 && (
                    <div className={styles.featuresList}>
                        <div className={styles.featuresTitle}>
                            {t('parts.thirdParty.features')}
                        </div>
                        <ul>
                            {part.features.map((feature, idx) => (
                                <li key={idx}>{feature}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* 配件详情展开按钮 */}
                {part.items && part.items.length > 0 && (
                    <>
                        <button
                            className={styles.expandButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpandedPart(isExpanded ? null : part.id);
                            }}
                        >
                            {isExpanded ? '▲ Hide Details' : '▼ View Details'}
                        </button>

                        {isExpanded && (
                            <div className={styles.packageItems}>
                                <ul>
                                    {part.items.map((item, idx) => (
                                        <li key={idx}>
                                            <span className={styles.itemName}>{item.name}</span>
                                            {item.quantity > 1 && (
                                                <span className={styles.itemQty}>×{item.quantity}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={styles.packageSection}>
            {categoryInfo?.description && (
                <p className={styles.categoryDescription}>
                    {t(categoryInfo.description)}
                </p>
            )}
            {categoryInfo?.note && (
                <p className={styles.categoryNote}>
                    {t(categoryInfo.note)}
                </p>
            )}

            {/* 官方配件列表 */}
            <div className={styles.packageGrid}>
                {officialParts.map((part) => {
                    const selected = isPartSelected(category, part.id);
                    const isExpanded = expandedPart === part.id;

                    // 跳过 "none" 选项的特殊处理
                    if (part.id === 'none') {
                        return (
                            <div
                                key={part.id}
                                className={`${styles.packageCard} ${styles.noneOption} ${selected ? styles.selected : ''}`}
                                onClick={() => updateConfig(category, part.id)}
                            >
                                <div className={styles.packageHeader}>
                                    <span className={styles.packageName}>{getPartName(part)}</span>
                                    <span className={styles.checkMark}>
                                        {selected ? '✓' : ''}
                                    </span>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={part.id}
                            className={`${styles.packageCard} ${selected ? styles.selected : ''} ${part.isBundlePackage ? styles.bundlePackage : ''}`}
                        >
                            <div
                                className={styles.packageHeader}
                                onClick={() => updateConfig(category, part.id)}
                            >
                                <div className={styles.packageTitleRow}>
                                    <span className={styles.packageName}>{getPartName(part)}</span>
                                    <span className={styles.checkMark}>
                                        {isMulti ? (selected ? '✓' : '+') : (selected ? '●' : '○')}
                                    </span>
                                </div>
                                <div className={styles.packageMeta}>
                                    <span className={styles.packagePrice}>
                                        ${part.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </span>
                                    {part.itemCount > 0 && (
                                        <span className={styles.itemCount}>
                                            {part.itemCount} {t('parts.package_items')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* 配件详情展开按钮 */}
                            {part.items && part.items.length > 0 && (
                                <>
                                    <button
                                        className={styles.expandButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedPart(isExpanded ? null : part.id);
                                        }}
                                    >
                                        {isExpanded ? '▲ Hide Details' : '▼ View Details'}
                                    </button>

                                    {isExpanded && (
                                        <div className={styles.packageItems}>
                                            <ul>
                                                {part.items.map((item, idx) => (
                                                    <li key={idx}>
                                                        <span className={styles.itemName}>{item.name}</span>
                                                        {item.quantity > 1 && (
                                                            <span className={styles.itemQty}>×{item.quantity}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* 第三方合作品牌配件区域 */}
            {thirdPartyParts.length > 0 && (
                <>
                    <div className={styles.sectionDivider}>
                        <span className={`${styles.sectionTitle} ${styles.partnerSectionTitle}`}>
                            Partner Brands
                        </span>
                    </div>
                    <div className={styles.packageGrid}>
                        {thirdPartyParts.map(renderThirdPartyCard)}
                    </div>
                </>
            )}
        </div>
    );
};

export default PartSelector;
